import React, { useState } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import inventoryService from '../../services/InventoryService.js';
import { Package } from "lucide-react";
import { useTranslation } from 'react-i18next';


const AddInventory = () => {
  const [formData, setFormData] = useState({
    item_name: '',
    category: '',
    sub_category: '',
    description: '',
    quantity: '',
    unit: '',
    unit_price: '',
    supplier_name: '',
    supplier_contact: '',
    warehouse_location: '',
    purchase_date: '',
    expiry_date: ''
  });
  
  const [itemImage, setItemImage] = useState(null);
  const [itemImagePreview, setItemImagePreview] = useState(null);
  const [invoiceAttachment, setInvoiceAttachment] = useState(null);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const categories = inventoryService.getCategories();
  const units = ['pieces', 'kg', 'liters', 'meters', 'boxes', 'packets', 'rolls', 'sets'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('Item image must be less than 5MB');
        return;
      }
      setItemImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setItemImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInvoiceChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('Invoice must be less than 5MB');
        return;
      }
      setInvoiceAttachment(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setInvoicePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeItemImage = () => {
    setItemImage(null);
    setItemImagePreview(null);
    const fileInput = document.getElementById('item-image-input');
    if (fileInput) fileInput.value = '';
  };

  const removeInvoice = () => {
    setInvoiceAttachment(null);
    setInvoicePreview(null);
    const fileInput = document.getElementById('invoice-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await inventoryService.create(formData, itemImage, invoiceAttachment);
      Alert.success('Inventory item created successfully!');
      setTimeout(() => navigate('/inventory'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create inventory item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-6">
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <Package className="w-5 h-5 text-buttonBg" /> Add New Inventory Item
                  </h1>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-sm shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
                  {/* Item Image Section */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Item Image</h3>
                    <div className="flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-6">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 border-2 border-gray-300 
                                      overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0 rounded-md">
                        {itemImagePreview ? (
                          <img src={itemImagePreview} alt="Item Preview" className="w-full h-full object-cover" />
                        ) : (
                          <Package className="w-10 h-10 text-gray-400" />
                        )}
                      </div>

                      <div className="flex-1 w-full">
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Upload Item Image
                        </label>
                        <input
                          id="item-image-input"
                          type="file"
                          accept="image/*"
                          onChange={handleItemImageChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm 
                                    focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-600 mt-1">Accepted formats: JPG, PNG, GIF, WebP (Max 5MB)</p>

                        {itemImagePreview && (
                          <button
                            type="button"
                            onClick={removeItemImage}
                            className="mt-2 text-xs sm:text-sm text-red-600 hover:text-red-700"
                          >
                            Remove Image
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section 1: Basic Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Basic Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Item Name <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="item_name"
                          value={formData.item_name}
                          onChange={handleChange}
                          placeholder="Enter item name"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Category <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        >
                          <option value="">Select Category</option>
                          {categories.map((cat) => (
                            <option key={cat.value} value={cat.value}>
                              {cat.label}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Sub Category
                        </label>
                        <input
                          type="text"
                          name="sub_category"
                          value={formData.sub_category}
                          onChange={handleChange}
                          placeholder="Enter sub category"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div className="sm:col-span-1">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Warehouse Location <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="warehouse_location"
                          value={formData.warehouse_location}
                          onChange={handleChange}
                          placeholder="Enter warehouse location"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:gap-6 mt-4">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Description
                        </label>
                        <textarea
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          placeholder="Enter item description"
                          rows="3"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Quantity and Pricing */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Quantity and Pricing</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Quantity <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="number"
                          name="quantity"
                          value={formData.quantity}
                          onChange={handleChange}
                          placeholder="Enter quantity"
                          min="0"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Unit <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="unit"
                          value={formData.unit}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        >
                          <option value="">Select Unit</option>
                          {units.map((unit) => (
                            <option key={unit} value={unit}>
                              {unit}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Unit Price (₹) <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="unit_price"
                          value={formData.unit_price}
                          onChange={handleChange}
                          placeholder="Enter unit price"
                          min="0"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Total Value (₹)
                        </label>
                        <input
                          type="text"
                          value={formData.quantity && formData.unit_price ? (formData.quantity * formData.unit_price).toFixed(2) : '0.00'}
                          readOnly
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm bg-gray-100 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Supplier Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Supplier Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Supplier Name
                        </label>
                        <input
                          type="text"
                          name="supplier_name"
                          value={formData.supplier_name}
                          onChange={handleChange}
                          placeholder="Enter supplier name"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Supplier Contact
                        </label>
                        <input
                          type="text"
                          name="supplier_contact"
                          value={formData.supplier_contact}
                          onChange={handleChange}
                          placeholder="Enter supplier contact"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Dates and Invoice */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Dates and Invoice</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Purchase Date
                        </label>
                        <input
                          type="date"
                          name="purchase_date"
                          value={formData.purchase_date}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Expiry Date
                        </label>
                        <input
                          type="date"
                          name="expiry_date"
                          value={formData.expiry_date}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:gap-6 mt-4">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Invoice Attachment
                        </label>
                        <input
                          id="invoice-input"
                          type="file"
                          accept="image/*,.pdf"
                          onChange={handleInvoiceChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-600 mt-1">Upload invoice (PDF or Image, Max 5MB)</p>
                        {invoicePreview && (
                          <div className="mt-3">
                            <div className="relative inline-block">
                              <img 
                                src={invoicePreview} 
                                alt="Invoice Preview" 
                                className="w-32 h-32 sm:w-40 sm:h-40 object-cover border-2 border-gray-300 rounded-md"
                              />
                              <button
                                type="button"
                                onClick={removeInvoice}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                title="Remove invoice"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonTxt bg-buttonBg hover:text-buttonBg hover:bg-buttonTxt border 
border-current rounded-sm  transition-all duration-300 font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Creating...' : 'Create Item'}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/inventory')}
                      className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt 
hover:bg-buttonBg border border-current rounded-sm  transition-all duration-300 font-medium whitespace-nowrap"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddInventory;